import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { MapPin, Briefcase, Building2, Search } from 'lucide-react';
import Header from '../components/Header';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function JobListing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const category = searchParams.get('category');
  const city = searchParams.get('city');
  const course = searchParams.get('course');
  const skill = searchParams.get('skill');
  const search = searchParams.get('search'); 

  const activeFilters = [search, category, city, course, skill].filter(Boolean);
  const currentFilter = activeFilters.join(' • '); 

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilteredJobs = async () => {
      setLoading(true);
      try {
        let apiUrl = '/public-jobs?';
        if (category) apiUrl += `category=${encodeURIComponent(category)}&`;
        if (city) apiUrl += `city=${encodeURIComponent(city)}&`;
        if (course) apiUrl += `course=${encodeURIComponent(course)}&`;
        if (skill) apiUrl += `skill=${encodeURIComponent(skill)}&`;
        if (search) apiUrl += `search=${encodeURIComponent(search)}&`; 

        const response = await api.get(apiUrl);
        let fetchedJobs = response.data;

        // FRONTEND SEARCH FILTER
        if (search) {
          const searchLower = search.toLowerCase();
          fetchedJobs = fetchedJobs.filter(job => 
            (job.title && job.title.toLowerCase().includes(searchLower)) || 
            (job.employer?.company_name && job.employer.company_name.toLowerCase().includes(searchLower)) ||
            (job.category && job.category.toLowerCase().includes(searchLower)) ||
            (job.city && job.city.toLowerCase().includes(searchLower))
          );
        }

        setJobs(fetchedJobs);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilteredJobs();
  }, [category, city, course, skill, search]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <div className="py-10 px-4 max-w-[1200px] mx-auto">
        
        {/* Filter Summary Header */}
        <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center justify-between animate-in fade-in slide-in-from-top-4 duration-500">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              {currentFilter ? (
                <>Search Results for: <span className="text-[#E5B500]">{currentFilter}</span></>
              ) : 'Explore Active Jobs'}
            </h1>
            <p className="text-gray-500 mt-1 text-sm font-medium">
              We found <span className="text-black font-bold">{jobs.length}</span> opportunities
            </p>
          </div>
          {currentFilter && (
            <button 
              onClick={() => navigate('/job-listing')} 
              className="mt-4 md:mt-0 text-sm font-bold text-blue-600 hover:underline transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-gray-400">
            <Search className="w-10 h-10 animate-pulse mb-4 text-[#FED547]" />
            <p className="font-semibold text-lg text-gray-600">Finding best matches...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.length > 0 ? (
              jobs.map((job, index) => {
                // 🌟 FIX: Updated Employer Name Logic to priority match JobDetails.jsx
                const employerName = job.employer?.company_name || job.company_name || job.employer?.name || "Confidential Company";
                const locationText = job.city ? (job.locality ? `${job.locality}, ${job.city}` : job.city) : (job.location || 'Not Specified');
                const expText = job.freshers_only ? "Fresher" : (job.experience || "Not Specified");

                return (
                  <div 
                    key={job.id} 
                    className="group bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col h-full hover:-translate-y-1.5 hover:shadow-lg hover:border-[#FED547] transition-all duration-300"
                    style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'both' }}
                  >
                    
                    {/* Basic Info: Title & Company */}
                    <div className="mb-4">
                      <h2 className="text-[17px] font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors line-clamp-1">
                        {job.title}
                      </h2>
                      <div className="flex items-center text-sm text-gray-500 mt-1.5 line-clamp-1">
                        <Building2 className="w-4 h-4 mr-1.5 text-gray-400 shrink-0" />
                        {employerName}
                      </div>
                    </div>

                    {/* Minimal Tags */}
                    <div className="flex flex-wrap gap-2 mb-5">
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-semibold">
                        {job.job_type || 'Full-Time'}
                      </span>
                      {job.work_location_type && (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-semibold">
                          {job.work_location_type}
                        </span>
                      )}
                    </div>
                    
                    {/* Core Core Details */}
                    <div className="space-y-3 mb-6 flex-1">
                      <div className="flex items-center text-gray-600 text-[13.5px] font-medium">
                        <span className="w-4 h-4 mr-2.5 text-gray-400 shrink-0 flex items-center justify-center font-bold text-base">₹</span>
                        <span className="truncate">{job.salary_range || 'Not Disclosed'}</span>
                      </div>
                      <div className="flex items-center text-gray-600 text-[13.5px] font-medium">
                        <MapPin className="w-4 h-4 mr-2.5 text-gray-400 shrink-0" />
                        <span className="truncate" title={locationText}>{locationText}</span>
                      </div>
                      <div className="flex items-center text-gray-600 text-[13.5px] font-medium">
                        <Briefcase className="w-4 h-4 mr-2.5 text-gray-400 shrink-0" />
                        <span className="truncate">{expText}</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button 
                      onClick={() => navigate(`/job-details/${job.id}`)}
                      className="w-full bg-[#FED547] hover:bg-[#000000] text-black hover:text-[#FED547] font-bold py-2.5 rounded-lg text-sm transition-colors mt-auto"
                    >
                      View Details
                    </button>

                  </div>
                );
              })
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
                <Search className="w-12 h-12 text-gray-300 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-1">No jobs found</h3>
                <p className="text-gray-500 font-medium mb-4 text-center">
                  Try adjusting your filters to find more opportunities.
                </p>
                <button 
                  onClick={() => navigate('/job-listing')}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}